/**
 * Quotes page
 * Main page for managing quotes/estimates
 */

import { useState, useEffect } from "react";
import { useServiceFlow } from "@/contexts";
import { Icon, QuoteModal, AssignTechnicianModal } from "@/components/ui";
import { formatCurrency } from "@/utils/formatters";
import { notifyAction, notifyError, notifySuccess } from "@/utils";
import { budgetsService } from "@/services/budgetsService";
import { plaguesService } from "@/services/plaguesService";
import { usersService } from "@/services/usersService";
import { servicesService } from "@/services/servicesService";
import { workOrdersService } from "@/services/workOrdersService";
import { serviceRequestsService } from "@/services/serviceRequestsService";
import styles from "./QuotesPage.module.css";

interface Quote {
  id: string;
  quoteNumber: string;
  clientName: string;
  clientId: string;
  date: string;
  validUntil: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  total: number;
  subtotal?: number;
  tax?: number;
  status: "draft" | "pending" | "sent" | "approved" | "rejected" | "expired";
  services: string[];
  lineItems?: Array<{
    id: string;
    serviceId?: string;
    description: string;
    quantity: number;
    unitPrice: number;
    discount?: number;
    total: number;
  }>;
  notes?: string;
  token?: string; // Token único para acceso público
  sentAt?: string; // Fecha de envío por email
  /** ID numérico de la plaga (string) */
  pestType?: string;
  rejectionReason?: string; // Motivo del rechazo si fue rechazado
}

const apiStatusToLocal = (status: string): Quote["status"] => {
  if (status === "sended") return "sent";
  if (status === "pending") return "draft";
  if (
    status === "draft" ||
    status === "approved" ||
    status === "rejected" ||
    status === "expired"
  ) {
    return status as Quote["status"];
  }
  return "draft";
};

const getStatusLabel = (status: Quote["status"]): string => {
  const labels = {
    draft: "Borrador",
    pending: "Pendiente",
    sent: "Enviado",
    approved: "Aprobado",
    rejected: "Rechazado",
    expired: "Expirado",
  };
  return labels[status];
};

const getStatusColor = (status: Quote["status"]): string => {
  const colors = {
    draft: "#6b7280",
    pending: "#a855f7",
    sent: "#3b82f6",
    approved: "#10b981",
    rejected: "#ef4444",
    expired: "#f59e0b",
  };
  return colors[status];
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

/**
 * Quotes page component
 */
export function QuotesPage() {
  const CLIENTS_PAGE_SIZE = 50;
  const { pendingQuote, setPendingQuote } = useServiceFlow();
  const [pendingServiceRequestApiId, setPendingServiceRequestApiId] = useState<
    number | null
  >(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [prefillQuote, setPrefillQuote] = useState<Quote | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [quoteToConvert, setQuoteToConvert] = useState<Quote | null>(null);
  const [plagues, setPlagues] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [clientsPage, setClientsPage] = useState(1);
  const [hasMoreClients, setHasMoreClients] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [technicians, setTechnicians] = useState<
    Array<{ id: string; name: string; initials: string }>
  >([]);
  const [apiServices, setApiServices] = useState<
    Array<{ id: string; name: string; price: number }>
  >([]);
  const mapAPIUserToClientOption = (u: {
    id: number;
    company?: string | null;
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    const fullName = [u.first_name, u.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return {
      id: String(u.id),
      name: (u.company && u.company.trim()) || fullName || u.email,
    };
  };

  // Load budgets from API on mount
  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [
          budgetsRes,
          plaguesRes,
          techniciansRes,
          servicesRes,
          clientsRes,
        ] = await Promise.all([
          budgetsService.list({ page_size: 100 }),
          plaguesService.list({ page_size: 100 }),
          usersService.list({ role: 3, page_size: 100 }),
          servicesService.list({ page_size: 200 }),
          usersService.list({ role: 2, page: 1, page_size: CLIENTS_PAGE_SIZE }),
        ]);
        const mapped: Quote[] = budgetsRes.results.map((b) => {
          const total = parseFloat(b.total ?? "0");
          const subtotal = parseFloat(b.subtotal ?? "0");
          return {
            id: String(b.id),
            quoteNumber: b.budget_number,
            clientName: b.client_name ?? `Cliente #${b.client}`,
            clientId: String(b.client),
            date: b.date,
            validUntil: b.valid_until,
            serviceStartDate: b.service_start_date ?? undefined,
            serviceEndDate: b.service_end_date ?? undefined,
            total,
            subtotal,
            tax: total - subtotal,
            status: apiStatusToLocal(b.status),
            services: b.items?.map((i) => i.service_name) ?? [],
            lineItems:
              b.items?.map((i) => ({
                id: String(i.id ?? Math.random()),
                description: i.service_name,
                serviceId: String(i.service),
                quantity: i.quantity,
                unitPrice: parseFloat(i.unit_price),
                total: parseFloat(i.line_subtotal),
              })) ?? [],
            pestType: b.plague ? String(b.plague) : undefined,
            notes: b.notes ?? undefined,
          };
        });
        setQuotes(mapped);
        if (mapped.length > 0) setSelectedQuote(mapped[0]);

        setPlagues(
          plaguesRes.results.map((p) => ({
            id: String(p.id),
            name: p.name,
            isRecurrent: p.is_recurrent ?? false,
          })),
        );
        setTechnicians(
          techniciansRes.results.map((u) => {
            const full = [u.first_name, u.last_name].filter(Boolean).join(" ");
            const words = full.trim().split(/\s+/);
            const initials =
              words.length >= 2
                ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
                : full.substring(0, 2).toUpperCase();
            return { id: String(u.id), name: full || u.email, initials };
          }),
        );
        setApiServices(
          servicesRes.results.map((s) => ({
            id: String(s.id),
            name: s.name,
            price: parseFloat(s.price),
          })),
        );
        setClients(clientsRes.results.map(mapAPIUserToClientOption));
        setClientsPage(1);
        setHasMoreClients(clientsRes.next !== null);
      } catch (err) {
        console.error("Error cargando presupuestos:", err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const loadMoreClients = async () => {
    if (isLoadingClients || !hasMoreClients) return;

    const nextPage = clientsPage + 1;
    try {
      setIsLoadingClients(true);
      const res = await usersService.list({
        role: 2,
        page: nextPage,
        page_size: CLIENTS_PAGE_SIZE,
      });

      setClients((prev) => {
        const merged = new Map(prev.map((c) => [c.id, c]));
        res.results.map(mapAPIUserToClientOption).forEach((client) => {
          merged.set(client.id, client);
        });
        return Array.from(merged.values());
      });
      setClientsPage(nextPage);
      setHasMoreClients(res.next !== null);
    } catch (err) {
      console.error("Error cargando más clientes:", err);
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Detect pending quote from service request
  useEffect(() => {
    if (pendingQuote) {
      // Check if quote already exists
      const exists = quotes.some((q) => q.quoteNumber === pendingQuote.id);
      if (exists) {
        setPendingQuote(null);
        return;
      }

      // Convert to QuotesPage format with proper structure
      const subtotal = pendingQuote.amount || 0;
      const tax = subtotal * 0.21;
      const total = subtotal + tax;

      const newQuote: Quote = {
        id: pendingQuote.id,
        quoteNumber: pendingQuote.id,
        clientName: pendingQuote.clientName,
        clientId: pendingQuote.clientId,
        date: new Date().toISOString().split("T")[0],
        validUntil: pendingQuote.validUntil.toISOString().split("T")[0],
        serviceStartDate: undefined,
        serviceEndDate: undefined,
        subtotal: subtotal,
        tax: tax,
        total: total,
        status: "draft",
        services: [pendingQuote.serviceType],
        pestType: pendingQuote.serviceType as any,
        notes: `Solicitud: ${pendingQuote.requestId}\n\n${pendingQuote.description}`,
        lineItems: [
          {
            id: "1",
            description: pendingQuote.serviceType,
            quantity: 1,
            unitPrice: subtotal,
            total: subtotal,
          },
        ],
      };

      // Open create modal with prefilled data (without entering edit mode)
      setPrefillQuote(newQuote);
      setEditingQuote(null);
      setShowQuoteModal(true);

      // Guardar apiId para borrar la solicitud tras crear el presupuesto
      if (pendingQuote.serviceRequestApiId) {
        setPendingServiceRequestApiId(pendingQuote.serviceRequestApiId);
      }

      // Clear pending quote
      setPendingQuote(null);
    }
  }, [pendingQuote, setPendingQuote, quotes]);

  const filteredQuotes = quotes.filter((quote) => {
    const matchesSearch =
      searchQuery === "" ||
      quote.quoteNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.clientName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "Todos" || getStatusLabel(quote.status) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDownloadPDF = async (quote: Quote) => {
    const numId = parseInt(quote.id, 10);
    if (isNaN(numId) || String(numId) !== quote.id) {
      notifyError({
        title: "Presupuesto no guardado",
        message: "El presupuesto debe estar guardado en el servidor para exportar el PDF.",
      });
      return;
    }

    try {
      await budgetsService.exportPdf(numId, `${quote.quoteNumber}.pdf`);
    } catch (error) {
      notifyError({
        title: "Error al exportar PDF",
        message:
          error instanceof Error
            ? error.message
            : "No se pudo descargar el PDF del presupuesto.",
      });
    }
  };

  const handleDeleteQuote = (quoteId: string) => {
    const quoteToDelete = quotes.find((q) => q.id === quoteId);
    notifyAction({
      title: "Eliminar presupuesto",
      description: `Se eliminará el presupuesto ${quoteToDelete?.quoteNumber ?? "seleccionado"}.`,
      buttonTitle: "Eliminar",
      onClick: () => {
        setQuotes((prevQuotes) => prevQuotes.filter((q) => q.id !== quoteId));
        if (selectedQuote?.id === quoteId) {
          setSelectedQuote(null);
        }
        // Delete from API (only for API-sourced budgets with numeric IDs)
        const numId = parseInt(quoteId, 10);
        if (!isNaN(numId) && String(numId) === quoteId) {
          budgetsService.remove(numId).catch(console.error);
        }
      },
    });
  };

  const handleEditQuote = (quote: Quote) => {
    setPrefillQuote(null);
    setEditingQuote(quote);
    setShowQuoteModal(true);
  };

  const handleCreateQuote = () => {
    setPrefillQuote(null);
    setEditingQuote(null);
    setShowQuoteModal(true);
  };

  const handleDuplicateQuote = (quote: Quote) => {
    const newQuote: Quote = {
      ...quote,
      id: String(Date.now()),
      quoteNumber: `PRE-${new Date().getFullYear()}-${String(quotes.length + 1).padStart(3, "0")}`,
      status: "draft",
      date: new Date().toISOString().split("T")[0],
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      serviceEndDate: quote.serviceEndDate,
      serviceStartDate: quote.serviceStartDate,
      token: undefined, // Los borradores no tienen token hasta que se envían
      sentAt: undefined,
    };
    setQuotes([...quotes, newQuote]);
    setSelectedQuote(newQuote);
  };

  const handleSendByEmail = (quoteId: string) => {
    const quote = quotes.find((q) => q.id === quoteId);
    if (!quote) return;

    // Generar token si no existe
    const token =
      quote.token || `qt_${Math.random().toString(36).substring(2, 18)}`;

    notifyAction({
      title: "Enviar presupuesto por email",
      description: `Se enviará ${quote.quoteNumber} a ${quote.clientName}.`,
      buttonTitle: "Enviar",
      onClick: () => {
        // Actualizar el presupuesto con el token y cambiar estado a "sent"
        setQuotes((prevQuotes) =>
          prevQuotes.map((q) =>
            q.id === quoteId
              ? {
                  ...q,
                  status: "sent",
                  token,
                  sentAt: new Date().toISOString(),
                }
              : q,
          ),
        );
        setSelectedQuote((prev) =>
          prev && prev.id === quoteId
            ? {
                ...prev,
                status: "sent",
                token,
                sentAt: new Date().toISOString(),
              }
            : prev,
        );

        // Call API send-email endpoint for API-sourced budgets
        const numId = parseInt(quoteId, 10);
        if (!isNaN(numId) && String(numId) === quoteId) {
          budgetsService.sendEmail(numId).catch(console.error);
        }

        notifySuccess(
          "Email enviado",
          `El presupuesto se envió correctamente a ${quote.clientName}.`,
        );
      },
    });
  };

  const handleSaveQuote = async (quoteData: Quote) => {
    try {
      const items = quoteData.lineItems
        ?.filter((item) => item.serviceId)
        .map((item) => ({
          service: Number(item.serviceId),
          quantity: item.quantity,
          discount:
            item.discount !== undefined ? String(item.discount) : undefined,
        }));

      const apiPayload = {
        client: Number(quoteData.clientId),
        date: quoteData.date,
        valid_until: quoteData.validUntil,
        service_start_date: quoteData.serviceStartDate || null,
        service_end_date: quoteData.serviceEndDate || null,
        plague: quoteData.pestType ? Number(quoteData.pestType) : null,
        notes: quoteData.notes || null,
        items,
      };

      if (editingQuote) {
        const numId = parseInt(editingQuote.id, 10);
        if (!isNaN(numId) && String(numId) === editingQuote.id) {
          await budgetsService.patch(numId, apiPayload);
        }
        setQuotes((prevQuotes) =>
          prevQuotes.map((q) =>
            q.id === editingQuote.id ? { ...q, ...quoteData } : q,
          ),
        );
        if (selectedQuote?.id === editingQuote.id) {
          setSelectedQuote((prev) => (prev ? { ...prev, ...quoteData } : null));
        }
      } else {
        const created = await budgetsService.create(apiPayload);
        const total = parseFloat(created.total ?? "0");
        const subtotal = parseFloat(created.subtotal ?? "0");
        const newQuote: Quote = {
          ...quoteData,
          id: String(created.id),
          quoteNumber: created.budget_number,
          status: apiStatusToLocal(created.status),
          total,
          subtotal,
          tax: total - subtotal,
        };
        setQuotes((prevQuotes) => [newQuote, ...prevQuotes]);
        setSelectedQuote(newQuote);
        // Borrar la solicitud de servicio vinculada si la hay
        if (pendingServiceRequestApiId !== null) {
          serviceRequestsService
            .remove(pendingServiceRequestApiId)
            .catch(console.error);
          setPendingServiceRequestApiId(null);
        }
      }
    } catch (err) {
      console.error("Error saving budget:", err);
    }
    setShowQuoteModal(false);
    setEditingQuote(null);
    setPrefillQuote(null);
  };

  const handleChangeStatus = (quoteId: string, newStatus: Quote["status"]) => {
    setQuotes((prevQuotes) =>
      prevQuotes.map((q) =>
        q.id === quoteId ? { ...q, status: newStatus } : q,
      ),
    );
    if (selectedQuote?.id === quoteId) {
      setSelectedQuote((prev) =>
        prev ? { ...prev, status: newStatus } : null,
      );
    }
  };

  const handleConvertToWorkOrder = (quote: Quote) => {
    setQuoteToConvert(quote);
    setShowAssignModal(true);
  };

  const handleAssignTechnician = async (
    technicianId: string,
    scheduledDate: string,
  ) => {
    if (!quoteToConvert) return;

    try {
      await workOrdersService.create({
        client: Number(quoteToConvert.clientId),
        assigned_technician: Number(technicianId),
        scheduled_date: scheduledDate,
        urgency: "low",
        plague: quoteToConvert.pestType
          ? Number(quoteToConvert.pestType)
          : null,
        notes: quoteToConvert.notes ?? null,
        budget: Number(quoteToConvert.id),
      });

      // Marcar presupuesto como aprobado
      handleChangeStatus(quoteToConvert.id, "approved");

      setShowAssignModal(false);
      setQuoteToConvert(null);

      notifySuccess(
        "Orden creada",
        `La orden de trabajo se asignó para el ${new Date(scheduledDate).toLocaleDateString("es-ES")}.`,
      );
    } catch (err) {
      console.error("Error creando orden de trabajo:", err);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.quotesPage}>
        <div className={styles.mainContent}>
          <p style={{ padding: "2rem" }}>Cargando presupuestos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.quotesPage}>
      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Gestión de Presupuestos</h2>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.filterButton}>
              <Icon name="filter" size={16} />
              Limpiar filtros
            </button>
            <button
              className={styles.newQuoteButton}
              onClick={handleCreateQuote}
            >
              <Icon name="plus" size={18} />
              Nuevo Presupuesto
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Estado:</label>
            <select
              className={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option>Todos</option>
              <option>Borrador</option>
              <option>Enviado</option>
              <option>Aprobado</option>
              <option>Rechazado</option>
              <option>Expirado</option>
            </select>
          </div>

          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar presupuesto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          {filteredQuotes.length > 0 ? (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th>N° PRESUPUESTO</th>
                  <th>CLIENTE</th>
                  <th>FECHA</th>
                  <th>VÁLIDO HASTA</th>
                  <th>IMPORTE</th>
                  <th>ESTADO</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {filteredQuotes.map((quote) => (
                  <tr
                    key={quote.id}
                    className={
                      selectedQuote?.id === quote.id ? styles.selected : ""
                    }
                    onClick={() => setSelectedQuote(quote)}
                  >
                    <td>
                      <span className={styles.quoteNumber}>
                        {quote.quoteNumber}
                      </span>
                    </td>
                    <td>
                      <span className={styles.clientName}>
                        {quote.clientName}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {formatDate(quote.date)}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {formatDate(quote.validUntil)}
                      </span>
                    </td>
                    <td>
                      <span className={styles.amount}>
                        {formatCurrency(quote.total)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: `${getStatusColor(quote.status)}20`,
                          color: getStatusColor(quote.status),
                        }}
                      >
                        {getStatusLabel(quote.status)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.editButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditQuote(quote);
                          }}
                          title="Editar"
                        >
                          <Icon name="edit" size={16} />
                        </button>
                        <button
                          className={styles.duplicateButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateQuote(quote);
                          }}
                          title="Duplicar"
                        >
                          <Icon name="document" size={16} />
                        </button>
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteQuote(quote.id);
                          }}
                          title="Eliminar"
                        >
                          <Icon name="xCircle" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>
              <Icon name="document" size={48} />
              <p>No se encontraron presupuestos</p>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      {selectedQuote && (
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarQuote}>
              <div className={styles.sidebarQuoteInfo}>
                <h3>{selectedQuote.quoteNumber}</h3>
                <p className={styles.sidebarClientName}>
                  {selectedQuote.clientName}
                </p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button
                className={styles.downloadButton}
                title="Descargar PDF"
                onClick={() => handleDownloadPDF(selectedQuote)}
              >
                <Icon name="download" size={18} />
              </button>
            </div>
          </div>

          <div className={styles.sidebarContent}>
            {/* Status */}
            <div className={styles.sidebarSection}>
              <div className={styles.statusCard}>
                <div className={styles.statusCardHeader}>
                  <span>ESTADO</span>
                  <span
                    className={styles.statusBadgeLarge}
                    style={{
                      backgroundColor: `${getStatusColor(selectedQuote.status)}20`,
                      color: getStatusColor(selectedQuote.status),
                    }}
                  >
                    {getStatusLabel(selectedQuote.status)}
                  </span>
                </div>
                <div className={styles.statusCardInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Fecha:</span>
                    <span className={styles.infoValue}>
                      {formatDate(selectedQuote.date)}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Válido hasta:</span>
                    <span className={styles.infoValue}>
                      {formatDate(selectedQuote.validUntil)}
                    </span>
                  </div>
                  {selectedQuote.pestType && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Tipo de plaga:</span>
                      <span
                        className={styles.infoValue}
                        style={{
                          color: "#059669",
                          fontWeight: "600",
                          backgroundColor: "#d1fae5",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          fontSize: "13px",
                        }}
                      >
                        {plagues.find((p) => p.id === selectedQuote.pestType)
                          ?.name ?? selectedQuote.pestType}
                      </span>
                    </div>
                  )}
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Importe:</span>
                    <span className={styles.infoValueBold}>
                      {formatCurrency(selectedQuote.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className={styles.sidebarSection}>
              <h4>SERVICIOS INCLUIDOS</h4>
              <div className={styles.servicesList}>
                {selectedQuote.services && selectedQuote.services.length > 0 ? (
                  selectedQuote.services.map((service, index) => (
                    <div key={index} className={styles.serviceItem}>
                      <Icon
                        name="checkCircle"
                        size={16}
                        className={styles.serviceIcon}
                      />
                      <span>{service}</span>
                    </div>
                  ))
                ) : (
                  <p style={{ color: "#6b7280", fontSize: "14px" }}>
                    No hay servicios especificados
                  </p>
                )}
              </div>
            </div>

            {/* Rejection Reason */}
            {selectedQuote.status === "rejected" &&
              selectedQuote.rejectionReason && (
                <div className={styles.sidebarSection}>
                  <h4 style={{ color: "#ef4444" }}>MOTIVO DEL RECHAZO</h4>
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderLeft: "4px solid #ef4444",
                      padding: "1rem",
                      borderRadius: "6px",
                      color: "#7f1d1d",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {selectedQuote.rejectionReason}
                  </div>
                </div>
              )}

            {/* Actions */}
            <div className={styles.sidebarActions}>
              {selectedQuote.status === "draft" && (
                <button
                  className={styles.sendButton}
                  onClick={() => handleSendByEmail(selectedQuote.id)}
                >
                  <Icon name="mail" size={18} />
                  Enviar por Email
                </button>
              )}
              {selectedQuote.status === "sent" && (
                <>
                  <button
                    className={styles.resendButton}
                    onClick={() => handleSendByEmail(selectedQuote.id)}
                  >
                    <Icon name="mail" size={18} />
                    Reenviar Email
                  </button>
                  <button
                    className={styles.approveButton}
                    onClick={() =>
                      handleChangeStatus(selectedQuote.id, "approved")
                    }
                  >
                    <Icon name="checkCircle" size={18} />
                    Marcar como Aprobado
                  </button>
                  <button
                    className={styles.rejectButton}
                    onClick={() =>
                      handleChangeStatus(selectedQuote.id, "rejected")
                    }
                  >
                    <Icon name="xCircle" size={18} />
                    Marcar como Rechazado
                  </button>
                </>
              )}
              {selectedQuote.status === "approved" && (
                <button
                  className={styles.convertButton}
                  onClick={() => handleConvertToWorkOrder(selectedQuote)}
                >
                  <Icon name="clipboard" size={18} />
                  Convertir en Orden de Trabajo
                </button>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Quote Modal */}
      <QuoteModal
        isOpen={showQuoteModal}
        // @ts-ignore - Quote type incompatibility needs full type refactor
        quote={editingQuote}
        // @ts-ignore - Quote type incompatibility needs full type refactor
        initialQuote={prefillQuote}
        clients={clients}
        clientsLoading={isLoadingClients}
        hasMoreClients={hasMoreClients}
        onLoadMoreClients={() => {
          void loadMoreClients();
        }}
        plagues={plagues}
        services={apiServices}
        onClose={() => {
          setShowQuoteModal(false);
          setEditingQuote(null);
          setPrefillQuote(null);
        }}
        // @ts-ignore - Quote type incompatibility needs full type refactor
        onSave={handleSaveQuote}
      />

      {/* Assign Technician Modal */}
      <AssignTechnicianModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setQuoteToConvert(null);
        }}
        onAssign={handleAssignTechnician}
        technicians={technicians}
        quoteName={quoteToConvert?.quoteNumber}
      />
    </div>
  );
}

export default QuotesPage;
